/*
 * Registrierportal für Endkunden der Web-HMI (docs/registrierportal.md).
 * Eigener Einstieg (portal.html), läuft unter registry.<domain> und spricht
 * nur /api/portal/*. White label: Texte, Farben, Logo und Rechtstexte kommen
 * aus der Inhaltspflege der Instanz; fehlt dort etwas, gilt das Markenpaket
 * des Frontends (BRAND).
 *
 * Datensparsam: keine Cookies, kein lokaler Speicher, keine fremden Schriften
 * oder Skripte. Token und Bestätigungscode stehen im Fragment und werden nach
 * dem Lesen aus der Adresszeile entfernt.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { BRAND } from '../brand/brand';
import { RichText } from './markdown';
import { consentDocuments, parseRoute, readableOn, validate, type Route } from './portalLogic';
import { isColor, type PortalContent, type PortalDocumentDto } from './types';

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const resp = await fetch(path, {
    method,
    credentials: 'omit',
    cache: 'no-store',
    headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let parsed: unknown = null;
  try { parsed = await resp.json(); } catch { /* leer */ }
  if (!resp.ok) {
    const msg = (parsed as { error?: string } | null)?.error
      ?? (resp.status === 429 ? 'Zu viele Versuche. Bitte etwas später erneut versuchen.' : 'Das hat gerade nicht geklappt. Bitte erneut versuchen.');
    throw new Error(msg);
  }
  return parsed as T;
}

function applyBranding(content: PortalContent) {
  const b = content.branding;
  const root = document.documentElement.style;
  const primary = isColor(b.primaryColor) ? b.primaryColor : null;
  if (primary) root.setProperty('--p-primary', primary);
  root.setProperty('--p-on-primary', isColor(b.primaryTextColor) ? b.primaryTextColor : readableOn(primary ?? '#16191d'));
  if (isColor(b.backgroundColor)) root.setProperty('--p-bg', b.backgroundColor);
  if (isColor(b.textColor)) root.setProperty('--p-text', b.textColor);
  document.title = `${content.texts.title} · ${b.productName || BRAND.productName}`;
}

export function PortalApp() {
  const [route] = useState<Route>(() => parseRoute(location.hash));
  const [content, setContent] = useState<PortalContent | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reading, setReading] = useState<PortalDocumentDto | null>(null);

  useEffect(() => {
    // Token nicht in der Adresszeile stehen lassen (Bildschirmfoto, Verlauf).
    if (location.hash) history.replaceState(null, '', location.pathname);
    call<PortalContent>('GET', '/api/portal/content?lang=de')
      .then((c) => { setContent(c); applyBranding(c); })
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, []);

  const logo = content?.branding.logoUrl ?? BRAND.logo?.onLight ?? null;
  const product = content?.branding.productName || BRAND.productName;

  return (
    <div class="p-page">
      <header class="p-header">
        {logo ? <img class="p-logo" src={logo} alt={content?.branding.logoAlt || product} /> : <span class="p-product">{product}</span>}
      </header>
      <main class="p-main">
        {loadError && <p class="p-error">{loadError}</p>}
        {!content && !loadError && <p class="p-muted">Lade …</p>}
        {content && !reading && (
          <>
            {route.view === 'register' && <Register content={content} token={route.token} onRead={setReading} />}
            {route.view === 'confirm' && <Confirm content={content} code={route.code} />}
            {route.view === 'home' && (
              <section class="p-card">
                <h1>{content.texts.title}</h1>
                <p>Bitte den QR-Code am Bedienfeld Ihrer Anlage scannen, um sich zu registrieren.</p>
              </section>
            )}
          </>
        )}
        {reading && (
          <section class="p-card p-doc">
            <button type="button" class="p-link" onClick={() => setReading(null)}>Zurück</button>
            <h1>{reading.title}</h1>
            <RichText source={reading.body} />
            <p class="p-muted">Fassung {reading.version} vom {new Date(reading.publishedAt).toLocaleDateString('de-AT')}</p>
            <button type="button" class="p-button" onClick={() => setReading(null)}>Zurück</button>
          </section>
        )}
      </main>
      {content && (
        <footer class="p-footer">
          {content.documents.filter((d) => d.kind === 'imprint' || d.kind === 'privacy' || d.kind === 'terms').map((d) => (
            <button type="button" key={d.versionId} class="p-link" onClick={() => { setReading(d); window.scrollTo(0, 0); }}>{d.title}</button>
          ))}
          {BRAND.poweredBy && <span class="p-muted">Technik: HVACloud</span>}
        </footer>
      )}
    </div>
  );
}

type InvitationCheck = { valid: boolean; reason: string | null; plantName: string | null; expiresAt: string | null };

function Register({ content, token, onRead }: { content: PortalContent; token: string; onRead: (d: PortalDocumentDto) => void }) {
  const [check, setCheck] = useState<InvitationCheck | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [accepted, setAccepted] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const consents = useMemo(() => consentDocuments(content.documents), [content]);

  useEffect(() => {
    call<InvitationCheck>('POST', '/api/portal/invitations/check', { token })
      .then(setCheck)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [token]);

  if (!content.ready) return <section class="p-card"><h1>{content.texts.title}</h1><p>{content.texts.notReady}</p></section>;
  if (done) return <section class="p-card"><h1>{content.texts.title}</h1><p>{content.texts.submittedText}</p></section>;
  if (!check) return error ? <p class="p-error">{error}</p> : <p class="p-muted">Prüfe den Code …</p>;
  if (!check.valid) return <section class="p-card"><h1>{content.texts.title}</h1><p>{content.texts.invalidInvitation}</p></section>;

  const toggle = (id: string, on: boolean) => {
    const next = new Set(accepted);
    if (on) next.add(id); else next.delete(id);
    setAccepted(next);
  };

  const submit = async (e: Event) => {
    e.preventDefault();
    const problem = validate({ firstName, lastName, email, phone, accepted }, consents);
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError(null);
    try {
      await call('POST', '/api/portal/registrations', {
        token, firstName, lastName, email, phone: phone.trim() || null, language: content.language,
        acceptedVersionIds: [...accepted],
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="p-card">
      <h1>{content.texts.title}</h1>
      {check.plantName && <p class="p-plant">Anlage: <b>{check.plantName}</b></p>}
      <p>{content.texts.intro}</p>
      <form class="p-form" onSubmit={(e) => void submit(e)} noValidate>
        <label>Vorname<input autocomplete="given-name" value={firstName} maxLength={100} required
                             onInput={(e) => setFirstName((e.target as HTMLInputElement).value)} /></label>
        <label>Nachname<input autocomplete="family-name" value={lastName} maxLength={100} required
                              onInput={(e) => setLastName((e.target as HTMLInputElement).value)} /></label>
        <label>E-Mail-Adresse<input type="email" autocomplete="email" value={email} maxLength={320} required
                                    onInput={(e) => setEmail((e.target as HTMLInputElement).value)} /></label>
        <label>Telefon <span class="p-muted">(freiwillig)</span><input type="tel" autocomplete="tel" value={phone} maxLength={40}
                             onInput={(e) => setPhone((e.target as HTMLInputElement).value)} /></label>
        {consents.map((d) => (
          <div class="p-consent" key={d.versionId}>
            <input type="checkbox" id={`c-${d.versionId}`} checked={accepted.has(d.versionId)}
                   onChange={(e) => toggle(d.versionId, (e.target as HTMLInputElement).checked)} />
            <label for={`c-${d.versionId}`}>
              {d.consentLabel ?? d.title}{d.consentMode === 'required' ? ' *' : ''}{' '}
              <button type="button" class="p-link" onClick={() => onRead(d)}>{d.title} lesen</button>
            </label>
          </div>
        ))}
        {consents.some((d) => d.consentMode === 'required') && <p class="p-muted">* Pflichtangabe</p>}
        {error && <p class="p-error" role="alert">{error}</p>}
        <button type="submit" class="p-button" disabled={busy}>{busy ? 'Sende …' : 'Registrieren'}</button>
      </form>
    </section>
  );
}

type ConfirmResult = { status: string; accountOutcome: string | null; webHmiUrl: string };

function Confirm({ content, code }: { content: PortalContent; code: string }) {
  const [result, setResult] = useState<ConfirmResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    call<ConfirmResult>('POST', '/api/portal/confirm', { code })
      .then(setResult)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [code]);
  if (error) return <section class="p-card"><h1>{content.texts.title}</h1><p class="p-error">{error}</p></section>;
  if (!result) return <p class="p-muted">Bestätige …</p>;
  return (
    <section class="p-card">
      <h1>{content.texts.title}</h1>
      <p>{content.texts.confirmedText}</p>
      {result.status === 'wartet_auf_freigabe' && <p>{content.texts.awaitingApprovalText}</p>}
      {result.status === 'abgeschlossen' && (
        <>
          <p>{content.texts.doneText}</p>
          <a class="p-button" href={result.webHmiUrl}>Zur Anlage</a>
        </>
      )}
    </section>
  );
}
